/* ============================================================
   SINGSPACE ADMIN - PAGE ESPACES (CRUD)
   Refactored for Clean Code and maintainability
   ============================================================ */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SpaceCard } from '../../components/spaces/SpaceCard';
import { SpacesFilters } from '../../components/spaces/SpacesFilters';
import { SpacesHeader } from '../../components/spaces/SpacesHeader';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useAdminSpaces } from '../../hooks/useAdminSpaces';
import { useSpacesFilters } from '../../hooks/useSpacesFilters';

export function SpacesPage() {
  const navigate = useNavigate();
  const { spaces, isLoading, error, toggleSpace, deleteSpace, duplicateSpace, pagination, page, setPage } =
    useAdminSpaces();

  const [actionInProgress, setActionInProgress] = useState(false);
  const {
    filterStatus,
    filterWorkflow,
    hasActiveFilters,
    filteredSpaces,
    resetFilters,
    setStatusFilter,
    setWorkflowFilter,
  } = useSpacesFilters(spaces);

  const handleToggle = async (id: string, field: string) => {
    try {
      setActionInProgress(true);
      await toggleSpace(id, field);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Supprimer l'espace "${name}" ? Cette action est irréversible.`)) return;
    try {
      setActionInProgress(true);
      await deleteSpace(id);
    } catch (err: any) {
      alert(`Erreur: ${err?.response?.data?.error || err.message}`);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      setActionInProgress(true);
      const newSpace = await duplicateSpace(id);
      navigate(`/spaces/${newSpace.id}/edit`);
    } finally {
      setActionInProgress(false);
    }
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-6 pb-12">
      <SpacesHeader totalSpaces={spaces.length} />

      {spaces.length > 0 && (
        <SpacesFilters
          filterStatus={filterStatus}
          filterWorkflow={filterWorkflow}
          hasActiveFilters={hasActiveFilters}
          onStatusChange={setStatusFilter}
          onWorkflowChange={setWorkflowFilter}
          onReset={resetFilters}
          filteredCount={filteredSpaces.length}
          totalCount={spaces.length}
        />
      )}

      {spaces.length === 0 ? (
        <EmptyState onAction={() => navigate('/spaces/new')} />
      ) : filteredSpaces.length === 0 ? (
        <NoResultsState onReset={resetFilters} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '2rem' }}>
          {filteredSpaces.map((space) => (
            <SpaceCard
              key={space.id}
              space={space}
              onEdit={(id) => navigate(`/spaces/${id}/edit`)}
              onToggle={handleToggle}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              actionInProgress={actionInProgress}
            />
          ))}
        </div>
      )}

      <Pagination pagination={pagination} currentPage={page} onPageChange={setPage} />
    </div>
  );
}

// --- Internal Sub-components for Clean Code ---

function LoadingState() {
  return (
    <div className="space-y-6 pb-12">
      <div className="bg-white rounded-xl p-16 text-center shadow-sm">
        <LoadingSpinner message="Chargement des espaces..." submessage="Veuillez patienter" size="md" />
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="space-y-6 pb-12">
      <div className="bg-white rounded-xl p-12 text-center shadow-sm border-2 border-red-100">
        <p className="text-lg font-semibold text-red-600 mb-2">Erreur de chargement</p>
        <p className="text-sm text-slate-500">{message}</p>
      </div>
    </div>
  );
}

function EmptyState({ onAction }: { onAction: () => void }) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        padding: '4rem 2rem',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,48,60,0.08)',
      }}
    >
      <p style={{ fontSize: '18px', fontWeight: 600, color: '#00303c', marginBottom: '8px' }}>Aucun espace configuré</p>
      <p style={{ fontSize: '14px', color: 'rgba(0,48,60,0.5)', marginBottom: '24px' }}>
        Créez votre premier espace pour commencer
      </p>
      <button
        onClick={onAction}
        style={{
          padding: '0.75rem 1.5rem',
          background: '#00758d',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontSize: '0.95rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Créer le premier espace
      </button>
    </div>
  );
}

function NoResultsState({ onReset }: { onReset: () => void }) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        padding: '4rem 2rem',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,48,60,0.08)',
      }}
    >
      <p style={{ fontSize: '16px', color: '#666' }}>Aucun espace ne correspond à vos filtres.</p>
      <button
        onClick={onReset}
        style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          background: '#f0f0f0',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
        }}
      >
        Réinitialiser les filtres
      </button>
    </div>
  );
}

function Pagination({
  pagination,
  currentPage,
  onPageChange,
}: {
  pagination: any;
  currentPage: number;
  onPageChange: (p: number) => void;
}) {
  if (!pagination || pagination.totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3rem', gap: '0.5rem' }}>
      {Array.from({ length: pagination.totalPages }, (_, i) => (
        <button
          key={i + 1}
          onClick={() => onPageChange(i + 1)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            background: currentPage === i + 1 ? '#00758d' : 'white',
            color: currentPage === i + 1 ? 'white' : '#666',
            cursor: 'pointer',
            fontWeight: 600,
            transition: 'all 0.2s',
          }}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
}
